import {
  DOMAINS_BY_SECTION,
  DOMAIN_LABELS,
  SatBenchmarkDto,
  SatCadenceDto,
  SatDomain,
  SatDomainMapEntryDto,
  SatDomainMasterySnapshotDto,
  SatMasteryBand,
  SatSection,
  SatStudyPlanDto,
} from '@/api/sat';

/**
 * Demo/empty DTOs for the open course home (specs/008 origin, relocated by 009).
 *
 * These are shaped exactly like the real API responses
 * (`SatDomainMapEntryDto[]`, `SatStudyPlanDto`) so the REAL course-home
 * components can render them: the empty shapes are the first-run state, the
 * demo shapes power the "See it fill in" preview beat. Data shaped exactly like the real API responses
 * (`SatDomainMapEntryDto[]`, `SatStudyPlanDto`). Nothing here is fetched,
 * persisted, or sent anywhere; it exists purely so the components have
 * props to render.
 *
 * Two states per surface:
 *  - `empty*()` — the true "awaiting you" shape a first-time visitor has:
 *    NOT_STARTED everywhere, no scores, no plan.
 *  - `demo*()` — a believable mid-course student, shown only during the
 *    course preview's "See it fill in" beat. Percentages come from
 *    `OPEN_DEMO_MASTERY`.
 */

/** Per-domain demo mastery — keyed by domain (NOT positional) so taxonomy
 *  reorders can never silently shift percentages onto the wrong domain.
 *  A believable mid-course spread: some strengths, one clear growth area.
 *  Single source of truth for the demo percentages; `demoDomainMap()` is
 *  built from these values. Static and deterministic — never fetched. */
export const OPEN_DEMO_MASTERY: Record<SatDomain, number> = {
  INFORMATION_AND_IDEAS: 82,
  CRAFT_AND_STRUCTURE: 74,
  EXPRESSION_OF_IDEAS: 66,
  STANDARD_ENGLISH_CONVENTIONS: 58,
  ALGEBRA: 91,
  ADVANCED_MATH: 71,
  PROBLEM_SOLVING_AND_DATA_ANALYSIS: 44,
  GEOMETRY_AND_TRIGONOMETRY: 63,
};

const ALL_DOMAINS: SatDomain[] = [
  ...DOMAINS_BY_SECTION.READING_AND_WRITING,
  ...DOMAINS_BY_SECTION.MATH,
];

function sectionOf(domain: SatDomain): SatSection {
  return DOMAINS_BY_SECTION.READING_AND_WRITING.includes(domain)
    ? 'READING_AND_WRITING'
    : 'MATH';
}

/** yyyy-MM-dd, `days` from right now (negative = in the past). Computed at
 *  call time (never at module scope) so nothing here goes stale between a
 *  test run and render. */
function isoDaysFromNow(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function emptySnapshot(): SatDomainMasterySnapshotDto {
  return {
    overall: {
      band: 'NOT_STARTED',
      scorePct: null,
      effectiveN: null,
      confidence: 'LOW',
      contributingSkillsCount: 0,
      totalSkillsInDomain: 7,
      insufficientReason: null,
    },
    recent: {
      scorePct: null,
      accuracyPct: null,
      evidenceCount: 0,
      windowDays: 30,
    },
    trend: 'NOT_ENOUGH_RECENT_EVIDENCE',
  };
}

/** True empty course map — a fresh ghost session's course home, before the
 *  Initial Diagnostic. Same shape SatCoursePath expects; NOT_STARTED and
 *  scoreless across all 8 domains. */
export function emptyDomainMap(): SatDomainMapEntryDto[] {
  return ALL_DOMAINS.map((domain) => ({
    domain,
    domainLabel: DOMAIN_LABELS[domain],
    section: sectionOf(domain),
    workflowStatus: 'NOT_STARTED',
    readyForMasteryCheck: false,
    masteredAt: null,
    lastActivityAt: null,
    lastMasteryCheckAttemptId: null,
    lastMasteryCheckScorePct: null,
    lastMasteryCheckAt: null,
    masterySnapshot: emptySnapshot(),
    workflowVsEvidence: { inSync: true, summary: 'No evidence yet.' },
    signals: [],
  }));
}

export function bandForPct(pct: number): SatMasteryBand {
  if (pct >= 85) return 'MASTERED_CANDIDATE';
  if (pct >= 70) return 'PROFICIENT';
  if (pct >= 55) return 'APPROACHING';
  if (pct >= 35) return 'DEVELOPING';
  return 'INSUFFICIENT_EVIDENCE';
}

function demoSnapshot(pct: number, contributingSkillsCount: number): SatDomainMasterySnapshotDto {
  return {
    overall: {
      band: bandForPct(pct),
      scorePct: pct,
      effectiveN: 24,
      confidence: 'MEDIUM',
      contributingSkillsCount,
      totalSkillsInDomain: 7,
      insufficientReason: null,
    },
    recent: {
      scorePct: pct,
      accuracyPct: pct,
      evidenceCount: 8,
      windowDays: 30,
    },
    trend: 'IMPROVING',
  };
}

/** The demo's "you are here" domain — kept in one place so the page and the
 *  factory never drift on which domain is active. It is the weakest area in
 *  `OPEN_DEMO_MASTERY`, because the course always works on the weakest area next. */
export const DEMO_ACTIVE_DOMAIN: SatDomain = 'PROBLEM_SOLVING_AND_DATA_ANALYSIS';

const DEMO_MASTERED_DOMAIN: SatDomain = 'ALGEBRA';
const DEMO_IN_PROGRESS_DOMAINS: SatDomain[] = [DEMO_ACTIVE_DOMAIN, 'GEOMETRY_AND_TRIGONOMETRY'];

/** Believable mid-course demo spread — matches the legacy `OPEN_DEMO_MASTERY`
 *  percentages per domain: a couple of domains IN_PROGRESS (including the
 *  active one), one MASTERED with a `masteredAt`, the rest carrying scores
 *  from the initial diagnostic but not yet actively worked. */
export function demoDomainMap(): SatDomainMapEntryDto[] {
  return ALL_DOMAINS.map((domain) => {
    const pct = OPEN_DEMO_MASTERY[domain];
    const mastered = domain === DEMO_MASTERED_DOMAIN;
    const inProgress = DEMO_IN_PROGRESS_DOMAINS.includes(domain);
    return {
      domain,
      domainLabel: DOMAIN_LABELS[domain],
      section: sectionOf(domain),
      workflowStatus: mastered ? 'MASTERED' : inProgress ? 'IN_PROGRESS' : 'NOT_STARTED',
      readyForMasteryCheck: false,
      masteredAt: mastered ? isoDaysFromNow(-9) : null,
      lastActivityAt: isoDaysFromNow(-2),
      lastMasteryCheckAttemptId: null,
      lastMasteryCheckScorePct: null,
      lastMasteryCheckAt: null,
      masterySnapshot: demoSnapshot(pct, mastered ? 7 : inProgress ? 5 : 3),
      workflowVsEvidence: { inSync: true, summary: 'On track.' },
      signals: [],
    };
  });
}

/** Initial-diagnostic completion timestamp for the demo state — this is
 *  what flips `SatCoursePath` into its "Built from your results ..."
 *  personalized copy instead of the pre-diagnostic lock message. */
export function demoInitialDiagnosticCompletedAt(): string {
  return isoDaysFromNow(-12);
}

function demoBenchmark(
  id: string,
  totalScore: number,
  cbTestNumber: number,
  daysAgo: number,
): SatBenchmarkDto {
  const takenAt = isoDaysFromNow(-daysAgo);
  return {
    id,
    source: 'IN_SYSTEM',
    totalScore,
    rwScore: Math.round(totalScore / 2) - 10,
    mathScore: Math.round(totalScore / 2) + 10,
    takenAt,
    cbTestNumber,
    partial: false,
    notes: null,
    questionResultCount: 98,
    evidenceCreated: 54,
    createdAt: `${takenAt}T12:00:00Z`,
  };
}

/** Believable mid-course study plan: a test date ~10 weeks out, a baseline
 *  and a more-recent benchmark showing real improvement (1140 → 1280), and
 *  a cadence pill that reads like the real engine's output. */
export function demoPlan(): SatStudyPlanDto {
  const cadence: SatCadenceDto = {
    recommendedIntervalDays: 14,
    nextTestDueOn: isoDaysFromNow(14),
    recommendedCbTestNumber: 5,
    retake: false,
    dressRehearsal: false,
    testsRemaining: 6,
    usedCbTestNumbers: [3, 4],
    baselineNeeded: false,
    rationale: 'STANDARD_PACE',
  };
  return {
    testDate: isoDaysFromNow(70), // ~10 weeks out
    targetScore: 1450,
    weeklyPaceHours: 6,
    gradeLevel: 11,
    benchmarks: [
      demoBenchmark('demo-benchmark-1', 1140, 3, 45),
      demoBenchmark('demo-benchmark-2', 1280, 4, 10),
    ],
    cadence,
  };
}

/** True empty plan — no test date, no scores, the cadence engine asking for
 *  a first baseline. */
export function emptyPlan(): SatStudyPlanDto {
  const cadence: SatCadenceDto = {
    recommendedIntervalDays: 21,
    nextTestDueOn: isoDaysFromNow(21),
    recommendedCbTestNumber: null,
    retake: false,
    dressRehearsal: false,
    testsRemaining: 8,
    usedCbTestNumbers: [],
    baselineNeeded: true,
    rationale: 'LONG_HORIZON_CAP',
  };
  return {
    testDate: null,
    targetScore: null,
    weeklyPaceHours: null,
    gradeLevel: null,
    benchmarks: [],
    cadence,
  };
}
